import React, { useState, useRef, useCallback, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button, { ButtonTypes } from 'devextreme-react/button';
import Form, { Item, Label, ButtonItem, ButtonOptions, RequiredRule, EmailRule } from 'devextreme-react/form';
import LoadIndicator from 'devextreme-react/load-indicator';
import notify from 'devextreme/ui/notify';
import { useAuth } from '../../../contexts/auth';
import { ThemeContext } from '../../../theme/theme';
import './LoginForm.scss';
import { loginFromMobiserver, sendToken, sendTokenFBS } from '../../../api/requests';
import { setAppToken, setFirmName, setImageURL, setUserName, setVendorId, setUserFirstName, setUserLastName, setUserEmail, setUserRef } from '../../../api/sessions';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from "react-google-recaptcha";
import { getVendorIdCookie, setVendorIdCookie } from '../../../api/cookies';

import mobiRoute from "../../../assets/mobiRoute.png";

function getButtonStylingMode(theme: string | undefined): ButtonTypes.ButtonStyle {
  return theme === 'dark' ? 'outlined' : 'contained';
}


export const LoginForm = ({ resetLink, createAccountLink }) => {
  const { t } = useTranslation();
  const [verified, setVerified] = useState(false);
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [loading, setLoading] = useState(false);
  const themeContext = useContext(ThemeContext);

  // vendorId ve rememberMe için state ekleyelim
  const [vendorId, setVendorrId] = useState("");
  const [rememberMe, setRememberMe] = useState(true); // Default olarak işaretli

  // Sayfa yüklendiğinde vendorId çerezini al ve rememberMe'yi ayarla
  useEffect(() => {
    const savedVendorId = getVendorIdCookie();
    if (savedVendorId) {
      setVendorrId(savedVendorId);
      setRememberMe(true); // Eğer çerez varsa "Beni Hatırla" seçili olsun
    }
  }, []);

  const formData = useRef({ userName: "", password: "" });

  const onChange = () => {
    setVerified(true);
  };

  async function onSubmit(e) {
    e.preventDefault();
    if (!verified) {
      notify(t("pleaseVerifyRecaptcha") || "Please verify that you are not a robot.", "warning", 3000);
      return;
    }

    const { userName, password } = formData.current;
    setLoading(true);
    await loginFromMobiserver(userName, password, vendorId).then(async (res) => {
      if (res.resCode == 200) {

        setAppToken(res.userData[0]["apiToken"]);
        setVendorId(vendorId);
        setUserName(userName);
        setFirmName(res.vendorData[0]["erpFirmName"] ?? "");
        setImageURL(res.vendorData[0]["imageUrl"] ?? "");

        // Ticket sistemi için kullanıcı bilgilerini kaydet
        setUserFirstName(res.userData[0]["userFirstName"] ?? "");
        setUserLastName(res.userData[0]["userLastName"] ?? "");
        setUserEmail(res.userData[0]["userEmail"] ?? "");
        setUserRef(String(res.userData[0]["userRef"] ?? ""));


        // Eğer rememberMe seçiliyse vendorId'yi çerez olarak kaydet
        if (rememberMe) {
          setVendorIdCookie(vendorId);
        }
        sendTokenFBS(res.userData[0]["userRef"], res.userData[0]["apiToken"], vendorId);

        notify(t("loginSuccessfull"), "success", 1500);

        // routeadmin.mobiapi.net sendToken isteği şimdilik yorumda
        // sendToken(res.userData[0]["userRef"], res.userData[0]["apiToken"], vendorId).then((result) => {
        //   if (result.resCode == 200) {
        //     window.location.href = "/";
        //   } else {
        //     notify(result.resMessage, "error", 3000);
        //   }
        // });
        window.location.href = "/";

      } else {
        notify(res.resMessage, "error", 3000);
      }
      setLoading(false);
    });
  }

  const onCreateAccountClick = useCallback(() => {
    navigate(createAccountLink);
  }, [navigate]);

  const userNameEditorOptions = { stylingMode: "filled", placeholder: t("userName"), labelMode: "hidden" };
  const vendorIdEditorOptions = {
    stylingMode: "filled",
    placeholder: t("vendorID"),
    labelMode: "hidden",
    value: vendorId,
    onValueChanged: (e) => setVendorrId(e.value),
  };
  const passwordEditorOptions = { stylingMode: "filled", placeholder: t("password"), mode: "password", labelMode: "hidden" };
  const rememberMeEditorOptions = {
    text: t("rememberMe"),
    value: rememberMe,
    onValueChanged: (e) => setRememberMe(e.value),
    elementAttr: { class: "form-text" }
  };

  return (
    <>
      <div className="login-hero-v1" style={{background: "linear-gradient(135deg, rgba(15,23,42,0.92) 0%, rgba(30,58,95,0.88) 50%, rgba(10,15,26,0.95) 100%), url('/assets/vrp.jpg') center/cover"}}>
        <div className="hero-decoration-v1">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
          <div className="decoration-grid"></div>
        </div>

        <div className="floating-elements">
          <div className="float-card float-card-1">
            <div className="float-icon success">
              <i className="dx-icon-check"></i>
            </div>
            <div className="float-text">
              <span className="float-title">Gerçek Zamanlı Takip</span>
              <span className="float-desc">Araç konumları anlık</span>
            </div>
          </div>
          <div className="float-card float-card-2">
            <div className="float-icon primary">
              <i className="dx-icon-map"></i>
            </div>
            <div className="float-text">
              <span className="float-title">Rota Optimize Edildi</span>
              <span className="float-desc">%18 tasarruf</span>
            </div>
          </div>
          <div className="float-card float-card-3">
            <div className="float-icon warning">
              <i className="dx-icon-chart"></i>
            </div>
            <div className="float-text">
              <span className="float-title">Detaylı Raporlama</span>
              <span className="float-desc">42 teslimat tamamlandı</span>
            </div>
          </div>
        </div>

        <div className="hero-content-v1">
          <img src={mobiRoute} alt="mobiRoute" className="hero-logo-v1" />
          <h1 className="hero-title-v1" style={{marginTop: 20}}>
            <span className="line-small">YENİ NESİL</span>
            <span className="line-large">Rota Yönetimi</span>
          </h1>
          <p className="hero-description-v1">
            Filo takibi, rota optimizasyonu ve teslimat yönetimini tek platformda birleştirin. Verimliliği artırın, maliyetleri düşürün.
          </p>

          <div className="hero-features-v1">
            <div className="feature-item-v1">
              <i className="dx-icon-car"></i>
              <span>Gerçek Zamanlı Takip</span>
            </div>
            <div className="feature-item-v1">
              <i className="dx-icon-map"></i>
              <span>Akıllı Rota Planlama</span>
            </div>
            <div className="feature-item-v1">
              <i className="dx-icon-chart"></i>
              <span>Detaylı Raporlama</span>
            </div>
          </div>
        </div>

        <div className="hero-footer-v1">
          <div className="hero-footer-tagline">GÜVENİLİR HIZLI ve VERİMLİ</div>
          <div className="hero-footer-bottom">
            <img src="/product/mobiRoute.png" alt="mobiRoute" className="hero-logo-v2" />
            <img src="/product/mobiOffice.png" alt="mobiOffice" className="hero-logo-v2" />
            <img src="/product/mobiShop.png" alt="mobiShop" className="hero-logo-v2" />
            <img src="/product/mobiSales.png" alt="mobiSales" className="hero-logo-v2" />
          </div>
        </div>
      </div>

      <form className="login-form" onSubmit={onSubmit}>
        <Form formData={formData.current} disabled={loading} showColonAfterLabel showRequiredMark={false}>
          <Item dataField={"vendorId"} editorType={"dxTextBox"} editorOptions={vendorIdEditorOptions}>
            <RequiredRule message={t("vendorIdRequired")} />
            <Label visible={false} />
          </Item>
          <Item dataField={"userName"} editorType={"dxTextBox"} editorOptions={userNameEditorOptions}>
            <RequiredRule message={t("userNameRequired")} />
            <Label visible={false} />
          </Item>
          <Item dataField={"password"} editorType={"dxTextBox"} editorOptions={passwordEditorOptions}>
            <RequiredRule message={t("passwordRequired")} />
            <Label visible={false} />
          </Item>
          <Item dataField="rememberMe" editorType="dxCheckBox" editorOptions={rememberMeEditorOptions}>
            <Label visible={false} />
          </Item>
          <Item>
            <center><ReCAPTCHA sitekey="6LdhPQssAAAAAJpA3kRT-2J8GbqbiS9Uj7dWf_aI" onChange={onChange} /></center>
          </Item>
          <ButtonItem>
            <ButtonOptions width="100%" type="default" useSubmitBehavior>
              <span className="dx-button-text">
                {loading ? <LoadIndicator width="24px" height="24px" visible /> : t("signIn")}
              </span>
            </ButtonOptions>
          </ButtonItem>
        </Form>
        <div className="reset-link hidden">
          <Link to={resetLink}>{t("forgotPassword")}</Link>
        </div>

        <Button
          className="btn-create-account"
          text={t("createAccount")}
          width="100%"
          visible={false}
          onClick={onCreateAccountClick}
          stylingMode={getButtonStylingMode(themeContext?.theme)}
        />
      </form>
    </>
  );
};
